/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';

import { DefaultTable } from 'src/components';

import { IBenefitsConfigVersion } from 'src/interfaces';

import { DataTableCellEditButton, AlertAdapter } from 'pg-ui-library';
import { FiCopy } from 'react-icons/fi';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import { useNavigate } from 'react-router-dom';

import { Container } from './styles';

import { useBenefitsConfigVersion } from './queries/benefits-config-version.query';
import { useSetBenefitsConfigVersion, useSetBenefitsConfigVersionEditModal } from '../benefits-config-version';
import { EventBadges } from './event-badges';

const customStyles = {
  headRow: {
    style: {
      border: 'none',
    },
  },
  headCells: {
    style: {
      color: '#9399B2',
      font: 'normal normal bold 16px Biotif',
    },
  },
  rows: {
    style: {
      font: 'normal normal normal 14px Biotif',
      color: '#1B1D35',
      borderRadius: '5px',
      padding: '16px 0 16px 0',
      '&:not(:last-of-type)': {
        borderBottomWidth: '0px',
      },
    },
    stripedStyle: {
      color: '#1B1D35',
      backgroundColor: '#F3F4F8',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'rgb(230, 244, 244)',
      borderBottomColor: '#FFFFFF',
      borderRadius: '25px',
      outline: '1px solid #FFFFFF',
    },
  },
  cells: {
    style: {
      justifyContent: 'left',
      width: '150px',
      '&:last-of-type': {
        justifyContent: 'right',
        paddingRight: '36px',
      },
    },
  },
  pagination: {
    style: {
      border: 'none',
      svg: {
        fill: 'black',
      },
    },
  },
};

export const benefitsConfigVersionTableColumns: TableColumn<IBenefitsConfigVersion>[] = [
  {
    name: 'Version',
    selector: (row: IBenefitsConfigVersion) => row.version || '',
    sortField: 'Version',
    width: '200px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Start Date',
    selector: (row: IBenefitsConfigVersion) => (row.startDate as string) || '',
    width: '200px',
    sortField: 'startDate',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'End Date',
    selector: (row: IBenefitsConfigVersion) => (row.endDate as string) || '',
    width: '200px',
    sortField: 'endDate',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Payroll Start Date',
    selector: (row: IBenefitsConfigVersion) => (row.payrollStartDate as string) || '',
    width: '200px',
    sortField: 'payrollStartDate',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Payroll End Date',
    selector: (row: IBenefitsConfigVersion) => (row.payrollEndDate as string) || '',
    width: '200px',
    sortField: 'payrollEndDate',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Description',
    selector: (row: IBenefitsConfigVersion) => row.description || '',
    sortField: 'Description',
    width: '200px',
    omit: false,
    sortable: true,
    reorder: true,
    wrap: true,
  },
  {
    name: 'Events',
    cell: (row: IBenefitsConfigVersion) => <EventBadges events={row.event || []} />,
    sortField: 'event',
    width: '250px',
    omit: false,
    sortable: false,
    reorder: true,
    wrap: true,
  },
];

export const BenefitsConfigsVersionTable: React.FC<{
  type: string;
  searchTerm: string;
  columns: TableColumn<IBenefitsConfigVersion>[];
}> = ({ searchTerm, columns, type }) => {
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [sortBy, setSortBy] = useState('plan');
  const [sortDirection, setSortDirection] = useState('');
  const companyId = useTypedSelector(state => state.config.data?.config?.selectedCompanyId) || '';
  const editButtonWrapper = (onClick: Function) => <DataTableCellEditButton size={30} onClick={() => onClick()} />;
  // const deleteButtonWrapper = (onClick: Function) => <DataTableCellDeleteButton size={30} onClick={() => onClick()} />;

  const duplicateButtonWrapper = (onClick: Function, isLoading: boolean = false) => (
    <button
      type="button"
      onClick={() => onClick()}
      disabled={isLoading}
      style={{
        background: 'none',
        border: 'none',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        padding: '5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '4px',
        transition: 'background-color 0.2s',
        opacity: isLoading ? 0.6 : 1,
      }}
      onMouseEnter={e => {
        if (!isLoading) {
          e.currentTarget.style.backgroundColor = '#f0f0f0';
        }
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
      title={isLoading ? 'Duplicating...' : 'Duplicate row'}
    >
      <FiCopy size={20} color={isLoading ? '#999' : '#666'} />
    </button>
  );

  // const editMutation = useEditBenefitVersion();
  // const deleteMutation = useDeleteBenefitVersion();

  const setIsOpen = useSetBenefitsConfigVersionEditModal();
  const setConfigVersionToEdit = useSetBenefitsConfigVersion();

  const handleDuplicate = (row: IBenefitsConfigVersion) => {
    // Set the row to be duplicated and open the modal
    setConfigVersionToEdit({
      ...row,
      id: undefined, // Clear the ID so it creates a new version
      originalId: row.id, // Store the original ID for duplication
    } as any);
    setIsOpen(true);
  };

  const columnsAndActions = [
    ...columns,
    {
      // eslint-disable-next-line no-unused-vars
      cell: (row: IBenefitsConfigVersion) => (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          {duplicateButtonWrapper(() => handleDuplicate(row), false)}
          {editButtonWrapper(() => {
            setConfigVersionToEdit(row);
            setIsOpen(true);
          })}
        </div>
      ),
    },
  ];

  const {
    data: benefitsItems,
    refetch,
    isLoading,
    isFetching,
  } = useBenefitsConfigVersion({ search: searchTerm, page, limit, sortBy, sortDirection, companyId, type });

  useEffect(() => {
    refetch();
  }, [searchTerm, page, limit, sortBy, sortDirection]);

  const handlePageChange = (nPage: number) => {
    setPage(nPage);
  };

  const handlePerRowsChange = async (newLimit: number, nPage: number) => {
    setPage(nPage);
    setLimit(newLimit);
  };

  const handleSort = async (column: TableColumn<IBenefitsConfigVersion>, newSortDirection: SortOrder) => {
    setSortBy(column.sortField as unknown as string);
    setSortDirection(newSortDirection);
  };

  return (
    <Container>
      <DefaultTable
        customStyles={customStyles}
        columns={columnsAndActions}
        data={benefitsItems?.data.items || []}
        progressPending={isLoading || isFetching}
        pagination
        paginationServer
        paginationTotalRows={benefitsItems?.data.meta.totalItems || 0}
        onChangeRowsPerPage={handlePerRowsChange}
        onChangePage={handlePageChange}
        onSort={handleSort}
        sortServer
        onRowClicked={row => {
          const id = row.id as string;
          navigate(`/benefits/edit/prices/${encodeURIComponent(id)}`, { replace: true });
        }}
      />
    </Container>
  );
};
